// 种植日历的算法层：从 Open-Meteo archive 的逐日实测，算出常年值、霜期、
// 播种→收获投影、室内育苗窗口。**node 与浏览器共用同一份**。
//
// 为什么要单独成文件：这套算法原来只长在 tools/build-seo.js 里，供预生成的 24 城静态页使用。
// 现在 /grow/ 上还有一个能查任意城市的交互工具（js/growtool.js），如果两边各写一份，
// 同一个城市的静态页与工具页迟早给出两种答案——那比只支持 24 个城市更糟。
// 所以算法只此一份，静态页与工具都从这里取；上层只负责取数与排版。
//
// 评分与画稿仍然直接用游戏本体的 sim.js / crops.js / plants.js（recommendCrops、
// plantArt），这里不复制它们，只补上"常年值"这一层——那是游戏本体不需要、
// 而种植日历必须有的东西（游戏看的是真实预报，日历看的是多年常年值）。
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root) root.GrowCalc = api;
})(typeof self !== 'undefined' ? self : null, function () {
  'use strict';

  const YEARS = 5;                       // 取近 5 年做气候常年值
  // archive 拉的四个变量：温度上下限驱动积温与霜冻，降水与日照进 recommendCrops
  const DAILY_VARS = 'temperature_2m_max,temperature_2m_min,precipitation_sum,sunshine_duration';

  // ── 日序号 ─────────────────────────────────────────────────────────────────
  // 真实累计天数，不用 30.44 天/月近似——近似值在月界有 1~2 天系统偏差，
  // 会把"终霜前 8 周"这类倒推结果排进相邻的错误月份（曾把 3 月 2 日排进 2 月）。
  // 闰年口径（2 月按 29 天）与 fromDOY 保持一致。
  const MDAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const MCUM = MDAYS.reduce((a, n) => (a.push(a[a.length - 1] + n), a), [0]);
  const DOY = md => { const [m, d] = md.split('-').map(Number); return MCUM[m - 1] + d; };
  const fromDOY = n => {
    n = ((Math.round(n) - 1) % 366 + 366) % 366 + 1;
    let m = 1, rest = n;
    while (m < 12 && rest > MDAYS[m - 1]) { rest -= MDAYS[m - 1]; m++; }
    return { m, d: Math.max(1, Math.round(rest)) };
  };
  const pad = n => String(n).padStart(2, '0');
  const mdOf = (m, d) => `${pad(m)}-${pad(d)}`;

  const MON_EN = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  // 三处页面原本各写了一份一模一样的日期格式化，统一到这里
  const fmtMD = (x, en) => en ? `${MON_EN[x.m]} ${x.d}` : `${x.m} 月 ${x.d} 日`;

  // ── 霜期：终霜日（春天最后一次 0°C 以下）与初霜日（秋天第一次）──────────────
  // 园艺规划的两个基本锚点，也是 "last frost date <城市>" 这类搜索的答案。
  // ⚠️ 常年值（多年平均）里霜冻会被抹平——都柏林 1 月平均最低约 3°C，按平均值算会得出
  // "全年无霜"的错误结论。所以必须用**逐年实际霜日**再取平均。
  // ⚠️ 南半球生长季跨年（春霜在 9~10 月、秋霜在次年 4~5 月），枢轴月与北半球相反。
  function frostSeason(frostDates, south) {
    if (!frostDates.length) return { none: true };
    const byYear = {};
    for (const date of frostDates) {
      const yr = date.slice(0, 4), mo = +date.slice(5, 7), md = date.slice(5);
      (byYear[yr] = byYear[yr] || []).push({ mo, md });
    }
    // 北半球：春霜=1~6月，秋霜=7~12月；南半球对调
    const springHalf = south ? (mo => mo >= 7) : (mo => mo <= 6);
    const lastSprings = [], firstAutumns = [];
    for (const rows of Object.values(byYear)) {
      const sp = rows.filter(r => springHalf(r.mo)).map(r => DOY(r.md));
      const au = rows.filter(r => !springHalf(r.mo)).map(r => DOY(r.md));
      if (sp.length) lastSprings.push(Math.max(...sp));
      if (au.length) firstAutumns.push(Math.min(...au));
    }
    const avg = a => a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;
    const ls = avg(lastSprings), fa = avg(firstAutumns);
    if (ls == null && fa == null) return { none: true };
    // 无霜期：终霜次日 → 初霜前一日（南半球跨年，直接用模 366 的差值）
    let freeDays = null;
    if (ls != null && fa != null) freeDays = Math.round(((fa - ls) % 366 + 366) % 366);
    return { lastSpring: ls != null ? fromDOY(ls) : null, firstAutumn: fa != null ? fromDOY(fa) : null, freeDays };
  }

  // ── 取数窗口与 URL ─────────────────────────────────────────────────────────
  // archive 有几天延迟，末端往回退 5 天；否则尾部会拿到一串 null。
  function archiveWindow() {
    const end = new Date(); end.setDate(end.getDate() - 5);
    const start = new Date(end); start.setFullYear(start.getFullYear() - YEARS);
    const iso = d => d.toISOString().slice(0, 10);
    return { start: iso(start), end: iso(end) };
  }
  function archiveParams(lat, lon) {
    const w = archiveWindow();
    return { latitude: lat, longitude: lon, start_date: w.start, end_date: w.end,
             daily: DAILY_VARS, timezone: 'auto' };
  }
  function archiveUrl(lat, lon) {
    const p = archiveParams(lat, lon);
    return 'https://archive-api.open-meteo.com/v1/archive?'
      + Object.keys(p).map(k => `${k}=${encodeURIComponent(p[k])}`).join('&');
  }

  // ── 常年值：近 N 年逐日实测，按"月内第几天"求均值 ───────────────────────────
  // 入参是 archive 响应里的 daily 对象；south 决定霜期的南北半球口径。
  // 纯函数——取数方式（node 直连上游 / 浏览器走 /api/wx 代理）与算法无关。
  function climateFromArchive(d, south) {
    // ⚠️ 月统计（月降水总量、"多少年里出现过霜冻"）只能用**完整的日历月**。
    // 取数窗口的两头必然把某个月切成两个半月，而它们会被当成两个独立年份计入：
    // 窗口 2021-07-26 ~ 2026-07-26 下，7 月出现在 6 个日历年里，其中 2021 与 2026
    // 各只有半个月的雨——月降水均值因此被摊薄了六分之一。线上 24 城的 7 月页
    // 全部中招（totalYears 都是 6），cityMonthPage 里那句"会落进 YEARS+1 个日历年"
    // 发现的正是这个症状，但当时只把显示夹住了，没堵住源头。
    // 这个工具把问题放大：它的窗口永远截止到 5 天前，**当前月永远是残月**，
    // 于是用户查自己城市的当月降水，看到的必然偏小。
    // 逐日常年值（normals）不受影响——那是按"月内第几天"取均值，与月是否完整无关。
    const t0 = d.time[0], t1 = d.time[d.time.length - 1];
    const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();   // m 为 1~12
    const monthComplete = (yr, mo) =>
      `${yr}-${pad(mo)}-01` >= t0 && `${yr}-${pad(mo)}-${pad(daysInMonth(+yr, mo))}` <= t1;

    // 按 月-日 归并；同时留下逐年极值，用来算"多少年里出现过霜冻"这类真实概率
    const byMD = {}, monthStats = {}, frostDates = [];
    for (let i = 0; i < d.time.length; i++) {
      const date = d.time[i];
      const md = date.slice(5), mo = +date.slice(5, 7), yr = date.slice(0, 4);
      const tmax = d.temperature_2m_max[i], tmin = d.temperature_2m_min[i];
      if (tmax == null || tmin == null) continue;
      const sun = d.sunshine_duration && d.sunshine_duration[i] != null ? d.sunshine_duration[i] / 3600 : null;
      const precip = (d.precipitation_sum && d.precipitation_sum[i]) || 0;
      (byMD[md] = byMD[md] || []).push({ tmax, tmin, precip, sun });
      if (tmin <= 0) frostDates.push(date);
      if (!monthComplete(yr, mo)) continue;      // 残月不进月统计
      const ms = monthStats[mo] = monthStats[mo] || { years: {}, precip: {}, tminAbs: Infinity };
      ms.years[yr] = ms.years[yr] || { frost: 0 };
      if (tmin <= 0) ms.years[yr].frost++;
      ms.precip[yr] = (ms.precip[yr] || 0) + precip;
      ms.tminAbs = Math.min(ms.tminAbs, tmin);
    }
    const mean = (arr, f) => { const v = arr.map(f).filter(x => x != null); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };
    const normals = {};
    for (const [md, rows] of Object.entries(byMD)) {
      normals[md] = { tmax: mean(rows, r => r.tmax), tmin: mean(rows, r => r.tmin),
                      precip: mean(rows, r => r.precip), sun: mean(rows, r => r.sun) };
    }
    const months = {};
    for (const [mo, ms] of Object.entries(monthStats)) {
      const yrs = Object.keys(ms.years);
      months[mo] = {
        frostYears: yrs.filter(y => ms.years[y].frost > 0).length,
        totalYears: yrs.length,
        precipSum: mean(Object.values(ms.precip).map(v => ({ v })), r => r.v),
        tminAbs: ms.tminAbs,
      };
    }
    return { normals, months, frost: frostSeason(frostDates, south) };
  }

  // ── 「这个月最值得种的」怎么选 ─────────────────────────────────────────────
  // ⚠️ 不能直接 slice(0, N)。气候温和的月份会有二三十种作物并列满分，此时 sort 是稳定的，
  // 前 N 名实际由 crops.js 里的**书写顺序**决定——作物表从 26 涨到 50 之后，广州 8 月
  // 推荐位被前 8 条挤满，苋菜、豇豆、苦瓜这些当季主力全被挤到 14 名开外，而它们同样是满分。
  // 所以：同分按"多久能收"排（快的先），再按类别限流，保证叶/根/果/花都露面——
  // 一个园丁要的是"这个月各类各推荐什么"，不是八种叶菜。
  // opts.season: {cropId: 该作物全年平均分}——给了就用"这个月比它自己平均好多少"
  // 做同分次序。没有它的话，同分只能比"谁长得快"，而这会让一座气候温和的城市
  // 12 个月推荐同一批作物（都柏林实测 11 个月一模一样），整张表就白做了。
  function pickTop(scored, opts) {
    const o = opts || {};
    const min = o.min != null ? o.min : 55;
    const limit = o.limit != null ? o.limit : 12;
    const perCat = o.perCategory != null ? o.perCategory : 4;
    // ⚠️ 窗台盆栽（indoor）不受室外天气影响，恒满分且成熟快——不限流的话它们会
    // 占满每个月的头几位，让"这个月适合种什么"在所有城市所有月份长得一样。
    // 但冷月它们确实是唯一答案，所以是限流不是排除：先只放 maxIndoor 个，
    // 户外作物不够填满时，末尾的补位再把它们放回来。
    const maxIndoor = o.maxIndoor != null ? o.maxIndoor : 1;
    const season = o.season;
    const edge = r => season ? r.score - (season[r.crop.id] != null ? season[r.crop.id] : r.score) : 0;
    const ranked = scored.filter(r => r.score >= min)
      .slice()
      .sort((a, b) => b.score - a.score || edge(b) - edge(a) || (a.estDays || 999) - (b.estDays || 999));
    const used = {};
    let indoorN = 0;
    const out = [];
    for (const r of ranked) {
      if (out.length >= limit) break;
      const cat = r.crop.category || '?';
      if ((used[cat] || 0) >= perCat) continue;
      if (r.crop.indoor && indoorN >= maxIndoor) continue;
      if (r.crop.indoor) indoorN++;
      used[cat] = (used[cat] || 0) + 1;
      out.push(r);
    }
    // 限流之后还没填满（例如隆冬只有窗台盆栽够格），就把剩下的按名次补上
    if (out.length < limit) {
      for (const r of ranked) {
        if (out.length >= limit) break;
        if (!out.includes(r)) out.push(r);
      }
    }
    return out;
  }

  // 每种作物的全年平均分——pickTop 的 season 参数用它衡量"这个月对它是不是旺季"
  function seasonBaseline(climate, recommendFn, crops) {
    const sum = {}, n = {};
    for (let m = 1; m <= 12; m++) {
      const days = monthDays(climate, m);
      if (!days.length) continue;
      for (const r of recommendFn(crops, days)) {
        sum[r.crop.id] = (sum[r.crop.id] || 0) + r.score;
        n[r.crop.id] = (n[r.crop.id] || 0) + 1;
      }
    }
    const out = {};
    for (const id in sum) out[id] = sum[id] / n[id];
    return out;
  }

  // 把某个月的常年值展开成 recommendCrops 需要的逐日数组
  function monthDays(climate, monthNum) {
    const mm = pad(monthNum);
    const out = [];
    for (const [md, v] of Object.entries(climate.normals)) {
      if (md.slice(0, 2) !== mm) continue;
      if (v.tmax == null) continue;
      out.push({ date: `2026-${md}`, tmax: v.tmax, tmin: v.tmin, precip: v.precip, sun: v.sun });
    }
    return out.sort((a, b) => a.date.localeCompare(b.date));
  }

  // ── 本月播种 → 预计收获日 → 赶不赶得上初霜 ─────────────────────────────────
  // 这是种植日历真正要回答的问题，也是让"7月"和"8月"结论**不同**而不只是小数点
  // 不同的东西：在都柏林 8 月播番茄，算下来要到 11 月才熟，而初霜 10 月底就到了——种不成。
  //
  // 与游戏本体同一个积温公式：每天贡献 max(0, min(日均温, tUpper) − tBase)，累计到 gddMature。
  // 差别只在这里走的是**全年常年值曲线**（跨月、可跨年），而不是把当月气候当常数外推。
  function sowProjection(climate, crop, monthNum, frost) {
    const norm = climate.normals;
    // 从当月 15 日起算：既代表"这个月种"，又避开月初月末的边界感
    let cur = new Date(Date.UTC(2025, monthNum - 1, 15));
    let gdd = 0;
    for (let i = 0; i < 400; i++) {
      const key = mdOf(cur.getUTCMonth() + 1, cur.getUTCDate());
      const v = norm[key] || norm['02-28'];
      if (v && v.tmax != null) {
        // 窗台盆栽走室内微气候——公式必须与 sim.js:147 和 sim.js:285 完全一致，
        // 否则同一页会出现"91 天"和"约 8 天可收"两个互相打架的数字
        const tmin = crop.indoor ? Math.min(22, Math.max(16, 14 + v.tmin * 0.3)) : v.tmin;
        const tmax = crop.indoor ? Math.min(26, Math.max(20, 17 + v.tmax * 0.3)) : v.tmax;
        const tmean = (tmax + tmin) / 2;
        gdd += Math.max(0, Math.min(tmean, crop.tUpper) - crop.tBase);
      }
      if (gdd >= crop.gddMature) {
        const harvest = { m: cur.getUTCMonth() + 1, d: cur.getUTCDate() };
        // 赶不赶得上初霜：只对"轻霜就受害"的作物成立（菠菜 −8、羽衣甘蓝这类耐寒的无所谓）；
        // 窗台盆栽不受室外天气影响
        let beatsFrost = true;
        if (!crop.indoor && crop.frostDamage > -5 && frost && frost.firstAutumn) {
          const hDOY = DOY(mdOf(harvest.m, harvest.d));
          const fDOY = DOY(mdOf(frost.firstAutumn.m, frost.firstAutumn.d));
          const sDOY = DOY(mdOf(monthNum, 15));
          // 播种日→收获日 这段区间里是否跨过了初霜日（按模 366 处理跨年）
          const span = ((hDOY - sDOY) % 366 + 366) % 366;
          const toFrost = ((fDOY - sDOY) % 366 + 366) % 366;
          beatsFrost = span <= toFrost;
        }
        // 赶不上初霜时**不报收获日**：模型会让积温在冬天停摆、开春接着累，
        // 于是算出"次年 4 月收获"——数学上没错，但苗 11 月就冻死了，当成建议是误导的
        return { days: i + 1, harvest: beatsFrost ? harvest : null, beatsFrost, matures: true };
      }
      cur = new Date(cur.getTime() + 86400000);
    }
    return { matures: false };
  }

  // 适合"室内育苗 → 终霜后移栽"的作物。园艺上的硬约束，不是数值能推出来的：
  // 茄科（番茄/辣椒）与瓜类（黄瓜/南瓜/西葫芦）是标准的育苗移栽作物；
  // 草莓按苗定植；万寿菊常规育苗。其余一律排除——根菜移栽会分叉，
  // 豆科/玉米直根受伤就缓不过来，土豆是薯块直接下地，大蒜洋葱用鳞茎/苗。
  const TRANSPLANT_OK = new Set(['tomato', 'pepper', 'cucumber', 'pumpkin', 'zucchini', 'strawberry', 'marigold', 'aubergine']);

  // ── 室内育苗窗口：按终霜日倒推 6~8 周 ───────────────────────────────────────
  // 冷凉城市的 1~3 月，露天页面只能一直说"什么都别种"，12 个月里有半年长得一模一样。
  // 但真实的园艺日历这几个月讲的是**育苗**。无霜地区不需要育苗，这一段自然不出现。
  function indoorStarts(crops, climate, monthNum) {
    const frost = climate.frost;
    if (!frost || frost.none || !frost.lastSpring) return [];
    const lsDOY = DOY(mdOf(frost.lastSpring.m, frost.lastSpring.d));
    // ⚠️ 月份窗口必须用同一个 DOY 函数取上下界（下月 1 号减 1），不能写 mStart+29：
    // 月份实际 28~31 天，对不齐会在月界留下空隙——曾因此让 DOY 61 同时落在
    // 2 月窗口 [31,60] 和 3 月窗口 [62,91] 之外，番茄/辣椒/南瓜被静默丢掉。
    const mStart = DOY(mdOf(monthNum, 1));
    const mEnd = monthNum === 12 ? 366 : DOY(mdOf(monthNum + 1, 1)) - 1;
    const out = [];
    for (const c of crops) {
      // ⚠️ 不能只按"怕霜 + 积温大"筛——那样会把土豆和甜玉米也算进来，
      // 而土豆是切块薯直接下地、甜玉米直播且极忌移栽；根菜类一移栽根就分叉。
      // 这类硬性农学约束在 crops.js 的数值里表达不出来，只能显式列白名单。
      if (!TRANSPLANT_OK.has(c.id)) continue;
      if (c.indoor || c.frostDamage <= -2 || c.gddMature < 700) continue;
      // 本地露天根本熟不了的（如都柏林的番茄），不能建议"终霜后移栽出去"——
      // 同一页另一段正说着"任何月份都攒不够积温、要进温室"，两句会直接打架。
      if (!sowProjection(climate, c, monthNum, frost).matures) continue;
      const weeks = c.gddMature >= 1000 ? 8 : 6;
      let start = lsDOY - weeks * 7;
      if (start < 1) start += 366;
      if (start >= mStart && start <= mEnd) out.push({ crop: c, weeks });
    }
    return out;
  }

  // 霜期的文字表述。三种情形都要说得住：两头齐全 / 只有一头（如墨尔本的霜只出现在 7~8 月）/ 全年无霜。
  // ⚠️ 口径要老实：ERA5 是格点再分析值，会把地形抹平——洼地、屋顶、无遮挡的菜畦实际结霜更早。
  function frostLine(frost, en) {
    const D = x => fmtMD(x, en);
    if (!frost || frost.none) {
      return en ? 'No night at or below 0°C in the last 5 years — frost is not a limiting factor here, and tender crops can stay out year-round.'
                : `过去 ${YEARS} 年没有出现过 0°C 及以下的夜晚——霜冻不是这里的限制因素，喜温作物可以全年露天。`;
    }
    const { lastSpring: ls, firstAutumn: fa, freeDays } = frost;
    if (ls && fa && freeDays) {
      return en ? `Average last spring frost around ${D(ls)}, first autumn frost around ${D(fa)} — roughly ${freeDays} frost-free days. Sow tender crops after the last frost date and make sure they finish before the first.`
                : `常年终霜约在 ${D(ls)}，初霜约在 ${D(fa)}，无霜期约 ${freeDays} 天。喜温作物要等终霜过后再下种，并且必须赶在初霜前收完。`;
    }
    const one = ls || fa;
    return en ? `Frost is rare and confined to midwinter (recorded around ${D(one)}). Most of the year is frost-free; only the coldest weeks need cover.`
              : `霜冻少见，只集中在隆冬（记录约在 ${D(one)} 前后）。全年大部分时间无霜，只有最冷的那几周需要覆盖。`;
  }

  // 本月处在霜期的哪一段——同一城市的 12 个月各不相同，也是玩家/园丁最先想知道的一句
  function frostStatusFor(frost, monthNum, en) {
    if (!frost || frost.none) return '';
    const { lastSpring: ls, firstAutumn: fa } = frost;
    if (!ls || !fa) return '';
    const cur = DOY(mdOf(monthNum, 15));
    const a = DOY(mdOf(ls.m, ls.d));
    const b = DOY(mdOf(fa.m, fa.d));
    const inFree = a < b ? (cur > a && cur < b) : (cur > a || cur < b);
    return inFree
      ? (en ? 'This month sits inside the frost-free window.' : '本月处在无霜期内。')
      : (en ? 'This month still falls inside the frost season.' : '本月仍在霜期之内。');
  }

  // "先别种"要说出真正卡住它的那一条。reasons[0] 有时是句好话
  // （都柏林 8 月的番茄：reasons[0] 是"正处适生区间"，可它其实一年也熟不了），
  // 挑出带负面信号的那条才不会自相矛盾。
  function limitReason(r, en) {
    const rs = r.reasons || [];
    const neg = rs.find(x => /偏低|偏高|霜|热应激|积温太慢|偏慢|too cold|too hot|frost|heat|Too little|Slow/.test(x));
    return neg || rs[0] || '';
  }

  return { YEARS, DAILY_VARS, MDAYS, MCUM, DOY, fromDOY, mdOf, fmtMD, MON_EN,
           frostSeason, archiveWindow, archiveParams, archiveUrl, climateFromArchive,
           monthDays, pickTop, seasonBaseline, sowProjection, TRANSPLANT_OK, indoorStarts,
           frostLine, frostStatusFor, limitReason };
});
